import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Filter,
  Loader2,
  Inbox,
  Trash2,
  RotateCcw,
  Eye,
  EyeOff,
  CheckSquare,
  Square,
  CalendarRange,
  X,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import AppTopbar from "@/components/AppTopbar";
import BottomNav from "@/components/BottomNav";
import AlertCard from "@/components/AlertCard";
import ConfirmDialog from "@/components/ConfirmDialog";
import HardDeleteDialog from "@/components/HardDeleteDialog";
import PurgeBeforeDialog from "@/components/PurgeBeforeDialog";
import { listAlerts } from "@/lib/alerts";
import {
  listAlertsAdmin,
  adminDeleteAlert,
  adminBulkDeleteAlerts,
  adminRestoreAlert,
  adminHardDeleteAlert,
  adminBulkHardDeleteAlerts,
  adminEmptyTrash,
} from "@/lib/admin";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTimeFr, timeAgoFr } from "@/lib/format";

const PAGE_SIZE = 20;

const LEVEL_FILTERS = [
  { id: "all", label: "Tous" },
  { id: "green", label: "Vert" },
  { id: "orange", label: "Orange" },
  { id: "red", label: "Rouge" },
];

const PERIOD_FILTERS = [
  { id: "7", label: "7 jours" },
  { id: "30", label: "30 jours" },
  { id: "all", label: "Tout" },
];

export default function History() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [level, setLevel] = useState("all");
  const [period, setPeriod] = useState("30");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Admin state
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // {target} for single
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [hardDel, setHardDel] = useState(null); // {target} for single hard delete
  const [hardBulk, setHardBulk] = useState(false);
  const [emptyTrashOpen, setEmptyTrashOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (p = 0, reset = false) => {
      setLoading(true);
      try {
        const res = isAdmin
          ? await listAlertsAdmin({
              status: "closed",
              level,
              limit: PAGE_SIZE + 1,
              skip: p * PAGE_SIZE,
              includeDeleted,
            })
          : await listAlerts({
              status: "closed",
              level,
              limit: PAGE_SIZE + 1,
              skip: p * PAGE_SIZE,
            });
        const fetched = res.items;
        const more = fetched.length > PAGE_SIZE;
        const trimmed = more ? fetched.slice(0, PAGE_SIZE) : fetched;
        setItems(reset ? trimmed : (prev) => [...prev, ...trimmed]);
        // Use a fresh callback because of the conditional above:
        if (reset) setItems(trimmed);
        setHasMore(more);
        setPage(p);
        setError(null);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [level, includeDeleted, isAdmin]
  );

  useEffect(() => {
    load(0, true);
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, includeDeleted, isAdmin]);

  const filtered = useMemo(() => {
    if (period === "all") return items;
    const days = parseInt(period, 10);
    const cutoff = Date.now() - days * 24 * 3600 * 1000;
    return items.filter((a) => {
      const t = new Date(a.closed_at || a.updated_at || a.created_at).getTime();
      return !Number.isNaN(t) && t >= cutoff;
    });
  }, [items, period]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const handleDelete = async () => {
    setBusy(true);
    try {
      await adminDeleteAlert(confirmDel.id);
      toast.success("Alerte supprimée.");
      setConfirmDel(null);
      load(0, true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    setBusy(true);
    try {
      const r = await adminBulkDeleteAlerts(Array.from(selected));
      toast.success(
        `${r.soft_deleted} alerte${r.soft_deleted > 1 ? "s" : ""} supprimée${
          r.soft_deleted > 1 ? "s" : ""
        }.${r.skipped ? " (" + r.skipped + " ignorée(s))" : ""}`
      );
      setConfirmBulk(false);
      clearSelection();
      load(0, true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async (a) => {
    try {
      await adminRestoreAlert(a.id);
      toast.success("Alerte restaurée.");
      load(0, true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleHardDelete = async () => {
    setBusy(true);
    try {
      await adminHardDeleteAlert(hardDel.id);
      toast.success("Alerte supprimée définitivement.");
      setHardDel(null);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(hardDel.id);
        return next;
      });
      load(0, true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleBulkHardDelete = async () => {
    setBusy(true);
    try {
      const archivedIds = filtered
        .filter((a) => selected.has(a.id) && a.is_deleted)
        .map((a) => a.id);
      if (!archivedIds.length) {
        toast.error("Aucune alerte archivée sélectionnée.");
        setHardBulk(false);
        return;
      }
      const r = await adminBulkHardDeleteAlerts(archivedIds);
      toast.success(
        `${r.hard_deleted} alerte${r.hard_deleted > 1 ? "s" : ""} supprimée${
          r.hard_deleted > 1 ? "s" : ""
        } définitivement.${r.skipped ? " (" + r.skipped + " ignorée(s))" : ""}`
      );
      setHardBulk(false);
      clearSelection();
      load(0, true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleEmptyTrash = async () => {
    setBusy(true);
    try {
      const r = await adminEmptyTrash();
      toast.success(
        `Corbeille vidée — ${r.hard_deleted} alerte${
          r.hard_deleted > 1 ? "s" : ""
        } supprimée${r.hard_deleted > 1 ? "s" : ""} définitivement.`
      );
      setEmptyTrashOpen(false);
      clearSelection();
      load(0, true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const archivedCount = useMemo(
    () => filtered.filter((a) => a.is_deleted).length,
    [filtered]
  );
  const selectedArchivedCount = useMemo(
    () => filtered.filter((a) => selected.has(a.id) && a.is_deleted).length,
    [filtered, selected]
  );
  const selectedActiveCount = useMemo(
    () => filtered.filter((a) => selected.has(a.id) && !a.is_deleted).length,
    [filtered, selected]
  );

  return (
    <div
      className="min-h-screen bg-[var(--sa-subtle)] pb-24"
      data-testid="history-screen"
    >
      <AppTopbar subtitle="Historique des alertes" />

      {/* Sticky bulk action bar (admin only, when selection > 0) */}
      {isAdmin && selected.size > 0 ? (
        <div
          data-testid="bulk-action-bar"
          className="sticky top-[60px] z-20 mx-auto mt-2 flex max-w-xl flex-wrap items-center justify-between gap-2 rounded-[14px] border border-red-200 bg-red-50 px-3 py-2 text-[13px] shadow-md"
          style={{ width: "calc(100% - 24px)" }}
        >
          <div className="flex items-center gap-2 text-red-800">
            <CheckSquare size={14} />
            <strong>{selected.size}</strong> sélectionnée
            {selected.size > 1 ? "s" : ""}
            {selectedArchivedCount > 0 ? (
              <span
                data-testid="bulk-archived-count"
                className="ml-1 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-slate-600"
              >
                {selectedArchivedCount} archivée
                {selectedArchivedCount > 1 ? "s" : ""}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={clearSelection}
              data-testid="bulk-clear-button"
              className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              <X size={12} /> Désélectionner
            </button>
            {selectedActiveCount > 0 ? (
              <button
                type="button"
                onClick={() => setConfirmBulk(true)}
                data-testid="bulk-delete-button"
                className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-[12px] font-bold text-white shadow-sm hover:bg-red-700"
              >
                <Trash2 size={12} /> Archiver ({selectedActiveCount})
              </button>
            ) : null}
            {selectedArchivedCount > 0 ? (
              <button
                type="button"
                onClick={() => setHardBulk(true)}
                data-testid="bulk-hard-delete-button"
                className="inline-flex items-center gap-1 rounded-full bg-red-700 px-3 py-1 text-[12px] font-bold text-white shadow-sm ring-1 ring-red-900/20 hover:bg-red-800"
              >
                <AlertTriangle size={12} /> Supprimer déf. ({selectedArchivedCount})
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <main className="mx-auto max-w-xl px-4 pt-5">
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="sa-card px-4 py-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-slate-500" />
              <h2 className="text-[12px] font-semibold uppercase tracking-wider text-slate-500">
                Filtres
              </h2>
            </div>
            {isAdmin ? (
              <div className="flex flex-wrap items-center gap-1.5">
                {includeDeleted ? (
                  <button
                    type="button"
                    onClick={() => setEmptyTrashOpen(true)}
                    disabled={archivedCount === 0}
                    data-testid="empty-trash-button"
                    className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-600 px-3 py-1 text-[12px] font-bold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 disabled:shadow-none"
                  >
                    <AlertTriangle size={12} /> Vider la corbeille ({archivedCount})
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setPurgeOpen(true)}
                  data-testid="purge-button"
                  className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-[12px] font-semibold text-red-700 transition hover:bg-red-100"
                >
                  <CalendarRange size={12} /> Purge périodique
                </button>
              </div>
            ) : null}
          </div>

          <div className="mt-3 flex flex-col gap-3">
            <FilterPills
              value={level}
              options={LEVEL_FILTERS}
              onChange={setLevel}
              testId="filter-level"
            />
            <FilterPills
              value={period}
              options={PERIOD_FILTERS}
              onChange={setPeriod}
              testId="filter-period"
            />
            {isAdmin ? (
              <button
                type="button"
                onClick={() => setIncludeDeleted((v) => !v)}
                data-testid="toggle-include-deleted"
                className="self-start inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
              >
                {includeDeleted ? <EyeOff size={12} /> : <Eye size={12} />}
                {includeDeleted ? "Masquer les supprimées" : "Voir les supprimées"}
              </button>
            ) : null}
          </div>
        </motion.section>

        {loading && items.length === 0 ? (
          <div className="mt-16 flex items-center justify-center text-slate-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="mt-10 flex flex-col items-center justify-center gap-3 rounded-[16px] border border-dashed border-slate-200 bg-white px-6 py-10 text-center"
            data-testid="history-empty"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-400">
              <Inbox size={22} />
            </div>
            <h3 className="text-[15px] font-semibold text-slate-900">
              Aucune alerte clôturée sur cette période
            </h3>
            <p className="text-[13px] text-slate-500">
              Les alertes clôturées apparaîtront ici.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-5 flex flex-col gap-3" data-testid="history-list">
              {filtered.map((a) =>
                isAdmin ? (
                  <AdminAlertRow
                    key={a.id}
                    alert={a}
                    selected={selected.has(a.id)}
                    onToggleSelect={() => toggleSelect(a.id)}
                    onDelete={() => setConfirmDel(a)}
                    onRestore={() => handleRestore(a)}
                    onHardDelete={() => setHardDel(a)}
                  />
                ) : (
                  <AlertCard key={a.id} alert={a} showClosed />
                )
              )}
            </div>
            {hasMore ? (
              <div className="mt-5 flex justify-center">
                <button
                  onClick={() => load(page + 1)}
                  disabled={loading}
                  data-testid="history-load-more"
                  className="rounded-full border border-slate-200 bg-white px-5 py-2 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                >
                  {loading ? "Chargement…" : "Charger plus"}
                </button>
              </div>
            ) : null}
          </>
        )}

        <p className="mt-10 pb-2 text-center text-[11px] font-medium text-slate-400">
          Propulsé par C l'Agence
        </p>
      </main>
      <BottomNav />

      <PurgeBeforeDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        onPurged={() => load(0, true)}
      />

      <ConfirmDialog
        open={Boolean(confirmDel)}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={`Supprimer cette alerte ?`}
        description={
          confirmDel
            ? `Confirmer la suppression de l'alerte de ${
                confirmDel.agent_snapshot?.full_name || "—"
              } (${confirmDel.short_id}). Elle n'apparaîtra plus dans l'historique.`
            : ""
        }
        confirmLabel="Supprimer"
        tone="danger"
        loading={busy}
        onConfirm={handleDelete}
        testId="confirm-delete-alert"
      />

      <ConfirmDialog
        open={confirmBulk}
        onOpenChange={setConfirmBulk}
        title={`Archiver ${selectedActiveCount} alerte${
          selectedActiveCount > 1 ? "s" : ""
        } ?`}
        description="Les alertes sélectionnées seront masquées de l'historique. Vous pourrez les restaurer ensuite via « Voir les supprimées »."
        confirmLabel={`Archiver (${selectedActiveCount})`}
        tone="danger"
        loading={busy}
        onConfirm={handleBulkDelete}
        testId="confirm-bulk-delete"
      />

      {/* HARD delete (single, bulk, empty-trash) — typed-confirmation */}
      <HardDeleteDialog
        open={Boolean(hardDel)}
        onOpenChange={(o) => !o && setHardDel(null)}
        title="Suppression définitive"
        description={
          hardDel
            ? `L'alerte ${
                hardDel.short_id
              } de ${
                hardDel.agent_snapshot?.full_name || "—"
              } sera supprimée définitivement de la base de données et ne pourra plus être restaurée.`
            : ""
        }
        loading={busy}
        onConfirm={handleHardDelete}
        testId="hard-delete-alert"
      />

      <HardDeleteDialog
        open={hardBulk}
        onOpenChange={setHardBulk}
        title={`Suppression définitive de ${selectedArchivedCount} alerte${
          selectedArchivedCount > 1 ? "s" : ""
        }`}
        description={`Les ${selectedArchivedCount} alerte${
          selectedArchivedCount > 1 ? "s" : ""
        } archivée${
          selectedArchivedCount > 1 ? "s" : ""
        } sélectionnée${
          selectedArchivedCount > 1 ? "s" : ""
        } ${
          selectedArchivedCount > 1 ? "seront supprimées" : "sera supprimée"
        } définitivement.`}
        confirmLabel={`Supprimer définitivement (${selectedArchivedCount})`}
        loading={busy}
        onConfirm={handleBulkHardDelete}
        testId="hard-delete-bulk"
      />

      <HardDeleteDialog
        open={emptyTrashOpen}
        onOpenChange={setEmptyTrashOpen}
        title="Vider la corbeille"
        description={`Toutes les alertes archivées (${archivedCount}) seront supprimées définitivement et ne pourront pas être restaurées.`}
        confirmLabel={`Vider la corbeille (${archivedCount})`}
        loading={busy}
        onConfirm={handleEmptyTrash}
        testId="hard-delete-empty-trash"
      />
    </div>
  );
}

function FilterPills({ value, options, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          data-testid={`${testId}-${o.id}`}
          className={`rounded-full px-3 py-1 text-[12px] font-semibold transition ${
            value === o.id
              ? "bg-[var(--sa-blue)] text-white"
              : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AdminAlertRow({
  alert,
  selected,
  onToggleSelect,
  onDelete,
  onRestore,
  onHardDelete,
}) {
  const isDeleted = Boolean(alert.is_deleted);
  const stopAndRun = (fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    fn();
  };
  return (
    <div className="relative">
      {/* Checkbox top-left — available on both active and archived rows */}
      <button
        type="button"
        onClick={stopAndRun(onToggleSelect)}
        aria-label={selected ? "Désélectionner" : "Sélectionner"}
        data-testid={`card-checkbox-${alert.short_id}`}
        className="absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border border-slate-300 bg-white shadow-sm hover:bg-slate-50"
      >
        {selected ? (
          <CheckSquare size={14} className="text-[var(--sa-blue)]" />
        ) : (
          <Square size={14} className="text-slate-400" />
        )}
      </button>

      {/* Top-right action buttons */}
      <div className="absolute right-2 top-2 z-10 flex items-center gap-1">
        {isDeleted ? (
          <>
            <button
              type="button"
              onClick={stopAndRun(onRestore)}
              data-testid={`card-restore-${alert.short_id}`}
              title="Restaurer"
              className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-100"
            >
              <RotateCcw size={11} /> Restaurer
            </button>
            <button
              type="button"
              onClick={stopAndRun(onHardDelete)}
              data-testid={`card-hard-delete-${alert.short_id}`}
              title="Supprimer définitivement"
              className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm hover:bg-red-700"
            >
              <AlertTriangle size={11} /> Suppr. déf.
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={stopAndRun(onDelete)}
            data-testid={`card-delete-${alert.short_id}`}
            title="Archiver"
            className="flex h-6 w-6 items-center justify-center rounded-full border border-red-200 bg-white text-red-500 shadow-sm hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div
        className={`pl-6 ${isDeleted ? "opacity-60" : ""} ${
          selected ? "ring-2 ring-[var(--sa-blue)] rounded-[16px]" : ""
        }`}
      >
        <AlertCard alert={alert} showClosed />
        {isDeleted ? (
          <div
            data-testid={`deleted-badge-${alert.short_id}`}
            className="-mt-2 mr-2 inline-block translate-y-[-6px] rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-slate-500"
            title={
              alert.deleted_at
                ? `Supprimée par ${alert.deleted_by_name || "—"} ${timeAgoFr(
                    alert.deleted_at
                  )} (${formatDateTimeFr(alert.deleted_at)})`
                : "Supprimée"
            }
          >
            Supprimée · {alert.deleted_by_name || "—"}
          </div>
        ) : null}
      </div>
    </div>
  );
}
